// WWI airship mooring mast — a tall tapering gunmetal lattice tower (four
// tilted corner legs pinched by square bands), a docking arm with a nose-cone
// cradle at the top, a banner-red mooring light and a small ground shed. The
// mast itself is the iconic vertical, readable at any camera yaw.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, gable, slab, poles } = styleV3;

const KHAKI = "#7a6f4e";
const FIELD = "#6d6f64";
const GUNMETAL = "#4a4d52";
const DOOR = "#3a3428";
const RED = "#b03a3a"; // banner red — the special/trade code, here the mooring light

const BASE = 0.12;         // foundation slab top
const MAST_H = 2.3;        // lattice top above the slab
const SPREAD0 = 0.62;      // leg half-spread at the base
const TAPER = 0.48;        // how far each leg leans in over the full height

// Leg half-spread at height y above the slab top.
function spreadAt(y: number): number {
  return SPREAD0 - TAPER * (y / MAST_H);
}

// The four tilted corner legs, from the slab up to `topY` above the slab.
function legs(topY: number): Part[] {
  const tilt = Math.atan(TAPER / MAST_H);
  const midY = topY / 2;
  const mid = spreadAt(midY);
  const len = topY * 1.045; // slant correction for the lean-in on both axes
  const out: Part[] = [];
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      out.push(cyl(0.032, 0.032, len, 5, GUNMETAL,
        [sx * mid, BASE + midY, sz * mid],
        [-sz * tilt, 0, sx * tilt]));
    }
  }
  return out;
}

// A square lattice ring pinching the legs at height `y` above the slab —
// four thin edge beams, open in the middle so the tower stays see-through.
function band(y: number): Part[] {
  const s = spreadAt(y);
  const w = 2 * s + 0.09;
  return [
    box(w, 0.045, 0.05, FIELD, [0, BASE + y, s]),
    box(w, 0.045, 0.05, FIELD, [0, BASE + y, -s]),
    box(0.05, 0.045, w, FIELD, [s, BASE + y, 0]),
    box(0.05, 0.045, w, FIELD, [-s, BASE + y, 0]),
  ];
}

// The mast head: platform, tip mast, banner-red light, docking arm + cradle.
function head(): Part[] {
  const topY = BASE + MAST_H;
  return [
    box(0.4, 0.07, 0.4, GUNMETAL, [0, topY + 0.035, 0]),
    cyl(0.028, 0.028, 0.34, 5, GUNMETAL, [0, topY + 0.24, 0]),
    { geometry: new THREE.SphereGeometry(0.055, 6, 5), color: RED,
      position: [0, topY + 0.44, 0] },
    box(0.07, 0.07, 0.58, GUNMETAL, [0, topY + 0.12, 0.33]),
    // The nose-cone cradle at the arm's end — a cup opening outward (+z).
    { geometry: new THREE.ConeGeometry(0.095, 0.16, 6), color: FIELD,
      position: [0, topY + 0.12, 0.66], rotation: [-Math.PI / 2, 0, 0] },
  ];
}

// The small winch shed on the ground beside the tower.
function shed(withRoof: boolean): Part[] {
  const cx = 0.66, cz = -0.62;
  const out: Part[] = [box(0.44, 0.3, 0.36, KHAKI, [cx, BASE + 0.15, cz])];
  if (withRoof) {
    out.push(gable(0.5, 0.42, 0.16, FIELD, cx, BASE + 0.3, cz, "x"));
    out.push(box(0.16, 0.22, 0.04, DOOR, [cx - 0.24, BASE + 0.11, cz]));
  }
  return out;
}

function foundation(): Part {
  return slab(1.94, 1.94);
}

export const airshipMast = defineModel({
  id: "airship-mast",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Airship Mooring Mast",
  tags: ["building", "wwi", "military", "aviation", "airship", "mast"],
  summary: "A tall tapering lattice mooring tower with a docking arm and a banner-red top light.",
  description:
    "A Great-War airship mooring mast: four gunmetal lattice legs taper to a " +
    "head platform carrying a docking arm with a nose-cone cradle and a " +
    "banner-red mooring light; a small winch shed sits at its foot.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [foundation()] },
    {
      id: "lower", label: "Lower",
      parts: () => [foundation(), ...legs(0.85), ...band(0.75), ...poles(1.7, 1.7, 1.0)],
    },
    {
      id: "upper", label: "Upper",
      parts: () => [
        foundation(), ...legs(1.7), ...band(0.75), ...band(1.5),
        ...shed(false), ...poles(1.7, 1.7, 1.8),
      ],
    },
    {
      id: "complete", label: "Complete",
      parts: () => [
        foundation(), ...legs(MAST_H), ...band(0.75), ...band(1.5), ...band(2.1),
        ...head(), ...shed(true),
      ],
    },
  ],
  animations: [],
});
